import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Phone, Lock, SignOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIN_KEY = "sar_admin_pin";

export default function Admin() {
  const [pin, setPin] = useState(() => sessionStorage.getItem(PIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [inputPin, setInputPin] = useState("");
  const [verifying, setVerifying] = useState(false);

  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Verify pin on mount if we have one stored
  useEffect(() => {
    const verify = async (p) => {
      try {
        await axios.post(`${API}/admin/verify`, { pin: p });
        setAuthed(true);
      } catch {
        sessionStorage.removeItem(PIN_KEY);
        setPin("");
        setAuthed(false);
      }
    };
    if (pin) verify(pin);
  }, [pin]);

  // Fetch inquiries after auth
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/inquiries`, {
          headers: { "X-Admin-Pin": pin },
        });
        setInquiries(res.data || []);
      } catch (e) {
        if (e?.response?.status === 401) {
          sessionStorage.removeItem(PIN_KEY);
          setPin("");
          setAuthed(false);
          setError("Session expired. Please enter PIN again.");
        } else {
          setError("Failed to load inquiries.");
        }
      } finally {
        setLoading(false);
      }
    };
    if (authed && pin) load();
  }, [authed, pin]);

  const submitPin = async (e) => {
    e.preventDefault();
    if (!inputPin) {
      toast.error("Enter the admin PIN.");
      return;
    }
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/verify`, { pin: inputPin });
      sessionStorage.setItem(PIN_KEY, inputPin);
      setPin(inputPin);
      setAuthed(true);
      toast.success("Access granted.");
    } catch (err) {
      toast.error("Incorrect PIN.");
    } finally {
      setVerifying(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(PIN_KEY);
    setPin("");
    setAuthed(false);
    setInputPin("");
    setInquiries([]);
  };

  // PIN gate
  if (!authed) {
    return (
      <div
        className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4"
        data-testid="admin-gate"
      >
        <form
          onSubmit={submitPin}
          noValidate
          className="w-full max-w-md p-8 bg-white border-2 border-slate-900 shadow-brutal"
          data-testid="admin-pin-form"
        >
          <div className="w-12 h-12 bg-[#EA580C] flex items-center justify-center mb-6">
            <Lock size={22} weight="duotone" className="text-white" />
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-2">
            Restricted Area
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900 leading-tight">
            Enter Admin PIN
          </h1>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            This page is for Shree Ambey Roadlines owners only.
          </p>

          <label className="block mt-6 text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
            PIN
          </label>
          <input
            type="password"
            value={inputPin}
            onChange={(e) => setInputPin(e.target.value)}
            data-testid="admin-pin-input"
            className="w-full bg-white border-2 border-slate-900 px-4 py-3 text-base font-mono font-bold text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#EA580C]/30 focus:border-[#EA580C] transition-brutal"
            placeholder="••••••••"
            autoFocus
          />

          <button
            type="submit"
            disabled={verifying}
            data-testid="admin-pin-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-[#EA580C] text-white font-bold uppercase tracking-wider text-sm px-6 py-4 border-2 border-slate-900 shadow-brutal hover-shadow-brutal transition-brutal disabled:opacity-60"
          >
            {verifying ? "Verifying…" : "Unlock"}
          </button>

          <a
            href="/"
            data-testid="admin-gate-back"
            className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft size={14} weight="bold" /> Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-page">
      <header className="border-b-2 border-slate-900 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
              Shree Ambey Roadlines
            </div>
            <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900">
              Quote Inquiries
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              data-testid="admin-back-link"
              className="inline-flex items-center gap-2 border-2 border-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-white shadow-brutal hover-shadow-brutal transition-brutal"
            >
              <ArrowLeft size={16} weight="bold" /> Site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border-2 border-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-slate-900 text-white transition-brutal hover:bg-[#EA580C]"
            >
              <SignOut size={16} weight="bold" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading && (
          <div className="text-slate-500 font-medium" data-testid="admin-loading">
            Loading inquiries…
          </div>
        )}
        {error && (
          <div className="text-red-600 font-medium" data-testid="admin-error">
            {error}
          </div>
        )}
        {!loading && !error && inquiries.length === 0 && (
          <div
            className="p-10 border-2 border-dashed border-slate-400 bg-white text-center text-slate-500 font-medium"
            data-testid="admin-empty"
          >
            No inquiries yet. Submitted quote requests will appear here.
          </div>
        )}

        {!loading && inquiries.length > 0 && (
          <div className="grid grid-cols-1 gap-4" data-testid="admin-inquiry-list">
            {inquiries.map((q) => (
              <div
                key={q.id}
                data-testid={`admin-inquiry-${q.id}`}
                className="p-6 bg-white border-2 border-slate-900 shadow-brutal"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-black text-xl text-slate-900">
                      {q.name}
                    </div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                      {new Date(q.created_at).toLocaleString()}
                    </div>
                  </div>
                  <a
                    href={`tel:${q.phone}`}
                    className="inline-flex items-center gap-2 bg-[#EA580C] text-white font-bold text-sm px-4 py-2"
                  >
                    <Phone size={14} weight="bold" /> {q.phone}
                  </a>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">
                      Pickup
                    </div>
                    <div className="text-slate-900 font-semibold">{q.pickup}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">
                      Drop
                    </div>
                    <div className="text-slate-900 font-semibold">{q.drop}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">
                      Payload
                    </div>
                    <div className="text-slate-900 font-semibold">
                      {q.payload_type || "—"}
                    </div>
                  </div>
                </div>
                {q.email && (
                  <div className="mt-3 text-sm text-slate-600">
                    <span className="font-bold">Email:</span> {q.email}
                  </div>
                )}
                {q.message && (
                  <div className="mt-3 p-3 bg-[#F1F5F9] text-sm text-slate-700 border border-slate-200">
                    {q.message}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
